import DeliveryNoteIcon from 'assets/dialogue-avatar-delivery-note.svg';
import { CustomerAuthLevel } from 'models/Customer';
import { DeliveryPreference, SafeDropInstruction } from 'models/DeliveryPreference';
import { DialogueCardData, DialogueType } from 'models/DialogCard';
import { AtlStatusValue, Milestone, ServiceCode } from 'models/shipments';
import { trackInteraction } from 'store/analytics/analytics.thunks';
import { AppDispatch } from 'store/store';

import { extLnkMyPostRedirectV2 } from '../../../../config/track-config';
import { AppLocation, getAtlLink } from '../../../../utils/window.utils';
import LiiaspWebToAppModal from '../../common/LiiaspWebToAppModal/LiiaspWebToAppModal';
import RedirectModal from '../../common/RedirectModal/RedirectModal';
import ArrowButton from './Arrows/ArrowButton';
import ArrowLink from './Arrows/ArrowLink';
const deliveryOptionDefaults = {
  type: DialogueType.DELIVERY_NOTE,
  icon: <DeliveryNoteIcon />,
};

const getCollectionLocationText = (isStarTrackPremiumArticle?: boolean) =>
  isStarTrackPremiumArticle ? 'Collection Point' : 'Post Office';

const atlIneligibleCards = (
  code?: string | null,
  safedropPref?: SafeDropInstruction | null,
  userOwnership?: boolean,
  actions?: JSX.Element,
  isStarTrackPremiumArticle?: boolean
): DialogueCardData | null => {
  if (!code) return null;

  const preferenceSet = (safedropPref === SafeDropInstruction.ON && userOwnership) || false;
  const preferenceStr = preferenceSet ? ' in your preferred location' : '';
  const collectionLocationText = getCollectionLocationText(isStarTrackPremiumArticle);

  switch (code) {
    case '1':
      return {
        ...deliveryOptionDefaults,
        analyticsId: 'atlIneligibleNoSignature',
        title: 'We won’t need a signature',
        content: <p>If nobody’s home, we’ll leave this parcel at the address – as long as there’s somewhere safe.</p>,
        actions,
      };
    case '2':
      return {
        ...deliveryOptionDefaults,
        analyticsId: 'atlIneligibleSignatureRequired',
        title: 'We’ll need a signature for this one',
        content: (
          <>
            <p>
              {`The sender requires a signature on delivery, so we won’t be able to leave this parcel${preferenceStr}.`}
            </p>
            <p>If nobody’s home, we’ll take it to a local {collectionLocationText} for collection.</p>
          </>
        ),
        actions,
      };
    case '7':
      return {
        ...deliveryOptionDefaults,
        analyticsId: 'atlIneligibleIDRequired',
        title: 'We’ll need a signature and ID',
        content: (
          <>
            <p>
              {`The sender requires a signature and ID check on delivery, so we won’t be able to leave this parcel${preferenceStr}.`}
            </p>
            <p>If nobody’s home, we’ll take it to a local {collectionLocationText} for collection.</p>
          </>
        ),
        actions,
      };
    default:
      return null;
  }
};

const safedropAvailableCards = (redirectEligible: boolean, actions?: JSX.Element) => ({
  ...deliveryOptionDefaults,
  analyticsId: 'safedropAvailable',
  title: 'We’ll ask for a signature',
  content: (
    <>
      <p>Won’t be home to sign? No problem.</p>
      <p>
        {redirectEligible
          ? 'Tell us what you’d like us to do.'
          : 'You can give us authority to leave this parcel somewhere safe.'}
        <sup>2</sup>
      </p>
    </>
  ),
  actions,
});

const safedropAppliedCards = (actions?: JSX.Element, location?: string | null) => ({
  ...deliveryOptionDefaults,
  analyticsId: 'safeDropApplied',
  title: `We’ll leave it ${location ? location.toLowerCase() : 'at a safe place on your premises'}`,
  content: <p>If nobody’s home, we’ll leave this parcel in your preferred location.</p>,
  actions,
});

const serviceCodeIDRequirementCards = (serviceCode: string, isStarTrackPremiumArticle?: boolean) => {
  const collectionLocationText = getCollectionLocationText(isStarTrackPremiumArticle);

  switch (serviceCode) {
    case ServiceCode.WINE:
      return {
        ...deliveryOptionDefaults,
        analyticsId: 'serviceCodeWineIDRequired',
        title: 'We’ll ask for ID and a signature',
        content: (
          <>
            <p>{`We can only hand over this parcel to the person it's addressed to. They’ll need to show valid ID proving they’re at least 18 years old and sign for the parcel.`}</p>
            <p>{`If they’re not home, we’ll take it to a local ${collectionLocationText} for collection.`}</p>
          </>
        ),
      };
    case ServiceCode.S8:
      return {
        ...deliveryOptionDefaults,
        analyticsId: 'serviceCodeS8IDRequired',
        title: 'We’ll ask for ID and a signature',
        content: (
          <>
            <p>{`We can only hand over this parcel to the person it's addressed to. They’ll need to show valid ID and sign for the parcel.`}</p>
            <p>{`If they’re not home, we’ll take it to a local ${collectionLocationText} for collection.`}</p>
          </>
        ),
      };
    default:
      return null;
  }
};

export function getDeliveryOptionsCard(
  authLevel: CustomerAuthLevel,
  atlEligible: boolean,
  articleRedirect: boolean,
  articleId: string,
  isLiiaspModalOpen: boolean,
  toggleLiiaspModal: (toggleState: boolean) => void,
  isRedirectModalOpen: boolean,
  toggleRedirectModal: (toggleState: boolean) => void,
  dispatch: AppDispatch,
  milestone: string | null,
  atlLocation?: string | null,
  safedropPreference?: DeliveryPreference | null,
  userOwnership?: boolean,
  atlStatus?: AtlStatusValue | null,
  safeDropIneligibilityCode?: string | null,
  serviceCode?: string | null,
  isStarTrackPremiumArticle?: boolean
): DialogueCardData | null {
  const { instruction } = safedropPreference || {};

  const serviceCodeRequiresID = !!serviceCode && [ServiceCode.WINE, ServiceCode.S8].includes(serviceCode);

  const atlText = atlStatus === AtlStatusValue.REQUESTED ? 'Change leave request' : 'Leave it in a safe place';
  const atlLink = getAtlLink(articleId);
  const actions =
    atlEligible || articleRedirect ? (
      <>
        {atlEligible && authLevel !== CustomerAuthLevel.ANONYMOUS && (
          <ArrowLink onClick={() => dispatch(trackInteraction(atlText.toLowerCase()))} href={atlLink}>
            {atlText}
          </ArrowLink>
        )}
        {atlEligible && authLevel === CustomerAuthLevel.ANONYMOUS && (
          <>
            <ArrowButton
              onClick={() => {
                dispatch(trackInteraction(atlText.toLowerCase()));
                toggleLiiaspModal(true);
              }}
            >
              {atlText}
            </ArrowButton>
            <LiiaspWebToAppModal
              isOpen={isLiiaspModalOpen}
              articleId={articleId}
              onClose={() => toggleLiiaspModal(false)}
            />
          </>
        )}
        {articleRedirect && (
          <>
            <ArrowButton
              onClick={() => {
                dispatch(trackInteraction('request redirect'));
                toggleRedirectModal(true);
              }}
            >
              Redirect it
            </ArrowButton>
            <RedirectModal
              isOpen={isRedirectModalOpen}
              handleClose={() => toggleRedirectModal(false)}
              handleContinue={() => AppLocation.assign(`${extLnkMyPostRedirectV2}/${articleId}`)}
            />
          </>
        )}
      </>
    ) : undefined;
  if (milestone === Milestone.PENDING) return null;
  if (serviceCodeRequiresID) {
    return serviceCodeIDRequirementCards(serviceCode, isStarTrackPremiumArticle);
  }

  if (!atlEligible) {
    return atlIneligibleCards(
      safeDropIneligibilityCode,
      instruction,
      userOwnership,
      actions,
      isStarTrackPremiumArticle
    );
  }
  if (userOwnership && atlStatus === AtlStatusValue.REQUESTED) return safedropAppliedCards(actions, atlLocation);
  return safedropAvailableCards(articleRedirect, actions);
}
